import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox as FluentCheckbox, Text, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import {
  BranchRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  WarningRegular,
  ClipboardTaskRegular,
  NumberSymbolRegular,
  TagRegular,
  MathFormulaRegular,
  MoneyRegular,
} from '@fluentui/react-icons';
import { connect } from 'react-redux';

// models
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';
import { ContractConceptPaymentSchedule } from '../../../../models/Entities/Contracts/ContractConceptPaymentSchedule';
import { NewContractConceptPaymentSchedule } from '../../../../models/Entities/Contracts/NewContractConceptPaymentSchedule';
import { StageDraft, StagePendingOperation, buildStageDraft, draftValueToNumber } from './ContractStageMultiselector.Types';

// styles
import './ContractStageMultiSelector.Style.css';

// components
import ContractStageMultiselectorItemComponent from './ContractStageMultiselectorItem/ContractStageMultiselectorItem.Component';

// Components functions
import * as ComponentFunctions from './ContractStageMultiselector.Functions';

/** Stages of a single contract branch, or the fallback bucket for stages without one. */
interface StageGroup {
  key: string;
  branchId: number | null;
  title: string;
  description: string | undefined;
  stages: ContractStage[];
}

const NO_BRANCH_KEY = 'no-branch';

/** How many visit names the "missing value" alert spells out before summarising. */
const MISSING_VALUE_NAMES_SHOWN = 5;

const toCheckedState = (selected: number, total: number): boolean | 'mixed' => {
  if (total > 0 && selected === total) return true;
  return selected > 0 ? 'mixed' : false;
};

const ContractStageMultiSelectorComponent = (props: ComponentFunctions.Props) => {

  const [drafts, setDrafts] = useState<Record<number, StageDraft>>({});
  const [pendingByStage, setPendingByStage] = useState<Record<number, StagePendingOperation>>({});
  const [savedStageIds, setSavedStageIds] = useState<number[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  // The sticky group headers park right below the sticky toolbar, whose height
  // changes with the error bar showing up or its text wrapping, so it is measured
  // rather than hardcoded.
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [toolbarHeight, setToolbarHeight] = useState<number>(0);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (toolbar === null || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => setToolbarHeight(toolbar.offsetHeight));
    observer.observe(toolbar);
    setToolbarHeight(toolbar.offsetHeight);

    return () => observer.disconnect();
  }, []);

  const sortedStages = useMemo(
    () => [...(props.Stages ?? [])].sort((a: ContractStage, b: ContractStage) => a.stageOrder - b.stageOrder),
    [props.Stages]
  );

  const scheduleByStage = useMemo(
    () => new Map<number, ContractConceptPaymentSchedule>(
      (props.PaymentSchedules ?? []).map((schedule: ContractConceptPaymentSchedule) => [schedule.contractStageId, schedule])
    ),
    [props.PaymentSchedules]
  );

  useEffect(() => {
    if (!(props.Stages?.length > 0)) {
      props.GetAllContractStagesByAddendumId(props.ContractId, props.ContractAddendumId);
    }
  }, [props.Stages, props.ContractId, props.ContractAddendumId]);

  // Nothing else in this screen loads the contract branches: the branch selector
  // that would is only mounted when "aplica por rama" is on. Without this the
  // grouping and the branch badges silently fall back to a flat list.
  useEffect(() => {
    if (props.ContractId && props.ContractAddendumId) {
      props.GetContractBranchsByAddendumId(props.ContractId, props.ContractAddendumId);
    }
  }, [props.ContractId, props.ContractAddendumId]);

  /**
   * Drafts are seeded from the saved payment schedules: on first sight, and again
   * once a schedule finishes loading — but never over a row the user already
   * touched, so an in-flight edit is not clobbered by a background refresh.
   */
  useEffect(() => {
    setDrafts((previousDrafts: Record<number, StageDraft>) => {
      const nextDrafts: Record<number, StageDraft> = {};
      let hasChanges = Object.keys(previousDrafts).length !== sortedStages.length;

      sortedStages.forEach((stage: ContractStage) => {
        const schedule = scheduleByStage.get(stage.id);
        const currentDraft = previousDrafts[stage.id];
        const scheduleAppearedOrVanished = currentDraft !== undefined
          && !currentDraft.isChanged
          && currentDraft.isSelected !== (schedule !== undefined);

        if (currentDraft === undefined || scheduleAppearedOrVanished) {
          nextDrafts[stage.id] = buildStageDraft(schedule);
          hasChanges = true;
          return;
        }

        nextDrafts[stage.id] = currentDraft;
      });

      return hasChanges ? nextDrafts : previousDrafts;
    });
  }, [sortedStages, scheduleByStage]);

  const onDraftChange = useCallback((stageId: number, patch: Partial<StageDraft>) => {
    setDrafts((previousDrafts: Record<number, StageDraft>) => {
      const currentDraft = previousDrafts[stageId];
      if (currentDraft === undefined) {
        return previousDrafts;
      }
      return { ...previousDrafts, [stageId]: { ...currentDraft, ...patch, isChanged: true } };
    });
  }, []);

  const applyBulkSelection = (stages: ContractStage[], isSelected: boolean) => {
    setDrafts((previousDrafts: Record<number, StageDraft>) => {
      const nextDrafts = { ...previousDrafts };
      stages.forEach((stage: ContractStage) => {
        const currentDraft = nextDrafts[stage.id];
        if (currentDraft !== undefined && currentDraft.isSelected !== isSelected) {
          nextDrafts[stage.id] = { ...currentDraft, isSelected: isSelected, isChanged: true };
        }
      });
      return nextDrafts;
    });
  };

  //#region Save

  // The save runs off a prop flip, so it reads the latest drafts through refs
  // instead of re-subscribing the effect to every keystroke.
  const draftsRef = useRef(drafts);
  const stagesRef = useRef(sortedStages);
  const scheduleRef = useRef(scheduleByStage);
  draftsRef.current = drafts;
  stagesRef.current = sortedStages;
  scheduleRef.current = scheduleByStage;

  const buildPaymentSchedule = (stage: ContractStage, draft: StageDraft, schedule: ContractConceptPaymentSchedule | undefined) => ({
    id: schedule?.id || undefined,
    contractConceptId: props.ContractConceptId || 0,
    contractStageId: stage.id,
    amountExpected: draftValueToNumber(draft.amount),
    settlementCoefficient: draftValueToNumber(draft.settlementCoefficient),
    price: draftValueToNumber(draft.inPersonPrice),
    remotePrice: draftValueToNumber(draft.remotePrice),
    phonePrice: draftValueToNumber(draft.phonePrice),
    screeningFailurePrice: draftValueToNumber(draft.screeningFailurePrice),
    settlementRemoteCoefficient: draftValueToNumber(draft.settlementRemoteCoefficient),
    settlementPhoneCoefficient: draftValueToNumber(draft.settlementPhoneCoefficient),
    settlementFollowUpCoefficient: draftValueToNumber(draft.settlementFollowUpCoefficient),
    settlementScreeningFailureCoefficient: draftValueToNumber(draft.settlementScreeningFailureCoefficient),
    priceWithoutOverhead: draftValueToNumber(draft.inPersonPrice),
    followUpPrice: draftValueToNumber(draft.followUpPrice),
    currency: props.currency
  });

  // Dispatches one write per touched stage. Untouched stages are reported as
  // saved right away so OnAllSaved does not wait on them.
  useEffect(() => {
    if (!props.ContractConceptId || !props.ConceptSaved) {
      return;
    }

    const nextPending: Record<number, StagePendingOperation> = {};
    const alreadySaved: number[] = [];

    stagesRef.current.forEach((stage: ContractStage) => {
      const draft = draftsRef.current[stage.id];
      const schedule = scheduleRef.current.get(stage.id);

      if (draft === undefined || !draft.isChanged) {
        alreadySaved.push(stage.id);
        return;
      }

      if (draft.isSelected) {
        const payload = buildPaymentSchedule(stage, draft, schedule);
        if (schedule !== undefined) {
          nextPending[stage.id] = 'update';
          props.UpdateContractConceptPaymentSchedule(
            props.ContractId,
            props.ContractAddendumId,
            payload as ContractConceptPaymentSchedule
          );
        } else {
          nextPending[stage.id] = 'add';
          props.AddContractConceptPaymenSchedule(
            props.ContractId,
            props.ContractAddendumId,
            payload as NewContractConceptPaymentSchedule
          );
        }
        return;
      }

      if (schedule !== undefined) {
        nextPending[stage.id] = 'delete';
        props.DeleteContractConceptPaymentSchedule(
          schedule.id,
          schedule.contractConceptId,
          props.ContractAddendumId,
          props.ContractId
        );
        return;
      }

      // not selected and never saved: nothing to do
      alreadySaved.push(stage.id);
    });

    setPendingByStage(nextPending);
    setSavedStageIds(alreadySaved);
  }, [props.ConceptSaved, props.ContractConceptId]);

  // Each pending write is resolved by watching the store settle for that stage.
  useEffect(() => {
    const pendingStageIds = Object.keys(pendingByStage).map(Number);
    if (pendingStageIds.length === 0) {
      return;
    }

    const failed = props.FailOnSaving || props.FailOnUpdating || props.FailOnDeleting;
    const resolvedStageIds = pendingStageIds.filter((stageId: number) => {
      if (failed) return true;
      const schedule = scheduleByStage.get(stageId);
      switch (pendingByStage[stageId]) {
        case 'add': return schedule !== undefined;
        case 'update': return schedule?.isUpdatedSuccessfully === true;
        default: return schedule === undefined;
      }
    });

    if (resolvedStageIds.length === 0) {
      return;
    }

    setPendingByStage((previousPending: Record<number, StagePendingOperation>) => {
      const nextPending = { ...previousPending };
      resolvedStageIds.forEach((stageId: number) => delete nextPending[stageId]);
      return nextPending;
    });
    setDrafts((previousDrafts: Record<number, StageDraft>) => {
      const nextDrafts = { ...previousDrafts };
      resolvedStageIds.forEach((stageId: number) => {
        if (nextDrafts[stageId] !== undefined) {
          nextDrafts[stageId] = { ...nextDrafts[stageId], isChanged: false };
        }
      });
      return nextDrafts;
    });
    setSavedStageIds((previousSaved: number[]) =>
      [...previousSaved, ...resolvedStageIds.filter((stageId: number) => !previousSaved.includes(stageId))]
    );
  }, [pendingByStage, scheduleByStage, props.FailOnSaving, props.FailOnUpdating, props.FailOnDeleting]);

  useEffect(() => {
    const allSaved = sortedStages.length > 0
      && sortedStages.every((stage: ContractStage) => savedStageIds.includes(stage.id));
    props.OnAllSaved(allSaved);
  }, [sortedStages, savedStageIds, props.OnAllSaved]);

  //#endregion Save

  /**
   * Stages are grouped by contract branch only when the contract actually defines
   * branches and at least one stage is linked to one; otherwise the flat list is kept.
   */
  const groups = useMemo<StageGroup[]>(() => {
    const branches: ContractBranch[] = props.Branches ?? [];
    const knownBranchIds = new Set(branches.map((branch: ContractBranch) => branch.id));
    const hasBranchedStages = sortedStages.some((stage: ContractStage) =>
      stage.branchId !== undefined && stage.branchId !== null && knownBranchIds.has(stage.branchId)
    );

    if (!hasBranchedStages) {
      return [];
    }

    const branchGroups: StageGroup[] = branches
      .map((branch: ContractBranch) => ({
        key: 'branch-' + branch.id,
        branchId: branch.id,
        title: branch.branchName,
        description: branch.branchDescription || undefined,
        stages: sortedStages.filter((stage: ContractStage) => stage.branchId === branch.id),
      }))
      .filter((group: StageGroup) => group.stages.length > 0);

    const stagesWithoutBranch = sortedStages.filter((stage: ContractStage) =>
      stage.branchId === undefined || stage.branchId === null || !knownBranchIds.has(stage.branchId)
    );

    if (stagesWithoutBranch.length === 0) {
      return branchGroups;
    }

    // stages that belong to no branch lead the list, the branches follow
    return [
      {
        key: NO_BRANCH_KEY,
        branchId: null,
        title: 'Visitas sin rama',
        description: undefined,
        stages: stagesWithoutBranch,
      },
      ...branchGroups,
    ];
  }, [sortedStages, props.Branches]);

  const isGrouped = groups.length > 0;

  const toggleGroup = (key: string) => {
    setCollapsedGroups((previousCollapsed: string[]) =>
      previousCollapsed.includes(key)
        ? previousCollapsed.filter((groupKey: string) => groupKey !== key)
        : [...previousCollapsed, key]
    );
  };

  const countSelected = (stages: ContractStage[]) =>
    stages.filter((stage: ContractStage) => drafts[stage.id]?.isSelected === true).length;

  const totalSelected = countSelected(sortedStages);

  /**
   * A selected visit whose applicable prices are all left at 0 would be billed at
   * nothing, so every section flags them. Only meaningful when the concept is
   * priced per visit — otherwise the prices are not editable here at all.
   */
  const findStagesMissingValue = (stages: ContractStage[]) => {
    if (!props.ShowInputValues) {
      return [];
    }

    return stages.filter((stage: ContractStage) => {
      const draft = drafts[stage.id];
      if (draft === undefined || !draft.isSelected) {
        return false;
      }

      const applicablePrices: string[] = [];
      if (props.applyToInFaceExecution) applicablePrices.push(draft.inPersonPrice);
      if (props.applyToRemoteExecution) applicablePrices.push(draft.remotePrice);
      if (props.applyToPhoneExecution) applicablePrices.push(draft.phonePrice);
      if (props.applyToFollowUpExecution) applicablePrices.push(draft.followUpPrice);
      if (props.applyToFailvisitExecution && stage.failSelectionEnabled) applicablePrices.push(draft.screeningFailurePrice);

      // no applicable modality means there is no price to fill in
      return applicablePrices.length > 0
        && applicablePrices.every((price: string) => draftValueToNumber(price) <= 0);
    });
  };

  const renderMissingValueAlert = (stages: ContractStage[]) => {
    if (stages.length === 0) {
      return null;
    }

    const namesShown = stages.slice(0, MISSING_VALUE_NAMES_SHOWN)
      .map((stage: ContractStage) => stage.stageName)
      .join(', ');
    const remaining = stages.length - MISSING_VALUE_NAMES_SHOWN;

    return (
      <MessageBar intent='warning' className='StageGroupAlert'>
        <MessageBarBody>
          <MessageBarTitle>
            {stages.length === 1
              ? '1 visita seleccionada sin valor monetario'
              : stages.length + ' visitas seleccionadas sin valor monetario'}
          </MessageBarTitle>
          {namesShown}{remaining > 0 ? ' y ' + remaining + ' más' : ''}
        </MessageBarBody>
      </MessageBar>
    );
  };

  const renderStage = (stage: ContractStage) => {
    const draft = drafts[stage.id];
    if (draft === undefined) {
      return null;
    }

    return (
      <li className='StageSelectorItem' key={stage.id}>
        <ContractStageMultiselectorItemComponent
          Stage={stage}
          BranchName={props.Branches?.find((branch: ContractBranch) => branch.id === stage.branchId)?.branchName}
          CurrencyCode={props.currency}
          ShowInputValues={props.ShowInputValues}
          Draft={draft}
          PendingOperation={pendingByStage[stage.id]}
          onDraftChange={onDraftChange}
          applyToRemoteExecution={props.applyToRemoteExecution}
          applyToInFaceExecution={props.applyToInFaceExecution}
          applyToPhoneExecution={props.applyToPhoneExecution}
          applyToFailvisitExecution={props.applyToFailvisitExecution}
          applyToFollowUpExecution={props.applyToFollowUpExecution}
        />
      </li>
    );
  };

  /**
   * Column names live here, once per list, instead of being repeated inside every
   * row. Only worth showing once something is selected, since the value columns
   * are empty otherwise.
   */
  const renderStageList = (stages: ContractStage[]) => (
    <ul className='StagesGrid'>
      {countSelected(stages) > 0 && (
        <li className={props.ShowInputValues ? 'StageListHeader' : 'StageListHeader NoPrices'}>
          <span className='StageListHeaderCell'>
            <ClipboardTaskRegular fontSize={14} />Visita
          </span>
          <span className='StageListHeaderCell'>
            <NumberSymbolRegular fontSize={14} />Cantidad
          </span>
          <div className='StageValueRow'>
            <span className='StageListHeaderCell'>
              <TagRegular fontSize={14} />Modalidad
            </span>
            <span className='StageListHeaderCell'>
              <MathFormulaRegular fontSize={14} />Coef. liq.
            </span>
            {props.ShowInputValues && (
              <span className='StageListHeaderCell'>
                <MoneyRegular fontSize={14} />Precio
              </span>
            )}
          </div>
        </li>
      )}
      {stages.map(renderStage)}
    </ul>
  );

  return (
    <>
      <div className='StageSelectorToolbar' ref={toolbarRef}>
        <div className='StageSelectorActions'>
          <FluentCheckbox
            label='Seleccionar / deseleccionar todas las visitas'
            checked={toCheckedState(totalSelected, sortedStages.length)}
            onChange={(_, data) => applyBulkSelection(sortedStages, data.checked === true)}
          />
          <Text className='StageSelectorActionsCounter'>
            {totalSelected} / {sortedStages.length} seleccionadas
          </Text>
        </div>

        {props.errorOnSaving && (
          <MessageBar intent='error' aria-multiline='true'>
            <MessageBarBody>
              <MessageBarTitle>Error</MessageBarTitle>
              {props.errorOnSaving.ErrorMessage}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>

      {isGrouped ? (
        <div className='StageGroupList'>
          {groups.map((group: StageGroup) => {
            const groupSelected = countSelected(group.stages);
            const isCollapsed = collapsedGroups.includes(group.key);
            const stagesMissingValue = findStagesMissingValue(group.stages);

            return (
              <section
                className={groupSelected > 0 ? 'StageGroup Active' : 'StageGroup'}
                key={group.key}
              >
                <header className='StageGroupHeader' style={{ top: toolbarHeight }}>
                  <button
                    type='button'
                    className='StageGroupToggle'
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={!isCollapsed}
                    aria-label={(isCollapsed ? 'Expandir ' : 'Contraer ') + group.title}
                  >
                    {isCollapsed
                      ? <ChevronRightRegular fontSize={16} />
                      : <ChevronDownRegular fontSize={16} />}
                  </button>
                  <FluentCheckbox
                    className='StageGroupCheckbox'
                    checked={toCheckedState(groupSelected, group.stages.length)}
                    onChange={(_, data) => applyBulkSelection(group.stages, data.checked === true)}
                    label={
                      <span className='StageGroupTitle'>
                        {group.branchId !== null
                          ? <BranchRegular className='StageGroupIcon' fontSize={16} />
                          : <TagRegular className='StageGroupIcon Neutral' fontSize={16} />}
                        {group.title}
                      </span>
                    }
                  />
                  {/* stays in the header so a collapsed section still shows the warning */}
                  {stagesMissingValue.length > 0 && (
                    <span
                      className='StageGroupWarningBadge'
                      title='Visitas seleccionadas sin valor monetario'
                    >
                      <WarningRegular fontSize={14} />
                      {stagesMissingValue.length}
                    </span>
                  )}
                  <Text className='StageGroupCounter'>
                    {groupSelected} / {group.stages.length}
                  </Text>
                </header>
                {!isCollapsed && (
                  <div className='StageGroupBody'>
                    {group.description && (
                      <Text className='StageGroupDescription'>{group.description}</Text>
                    )}
                    {renderMissingValueAlert(stagesMissingValue)}
                    {renderStageList(group.stages)}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <>
          {renderMissingValueAlert(findStagesMissingValue(sortedStages))}
          {renderStageList(sortedStages)}
        </>
      )}
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ContractStageMultiSelectorComponent as any);
